import React, { useEffect, useCallback, useState } from 'react';
import {
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Alert,
  FormFeedback,
  Button,
} from 'reactstrap';
import debounce from 'lodash/debounce';
import moment from 'moment';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useMutation, useQueryClient } from 'react-query';
import { updateMember, addMember, getMemberByIdNumberApi, getMemberByMobileApi } from 'api/member';
import { getAllGroupApi } from 'api/group';
import { getSearchColumns } from 'utils/tools';
import { useRole } from 'hooks/useRole';
import { useAgeLimits } from 'hooks/useAgeLimits';
import Select from 'react-select';
import { useSelector, useDispatch } from 'react-redux';
import {
  getSubCounties,
  getWards,
  getCounties,
  getRegions,
} from 'store/location/actions';
import Swal from 'sweetalert2';
import { DateField, SelectField } from 'components/Common/fields';

const MemberDetailsTab = ({
  onNext,
  member,
  onChangeMemberId = () => { },
  mutateMember,
  onSetMember,
}) => {
  const dispatch = useDispatch();
  const isNew = !member?.memberId;
  const [errorMessage, setErrorMessage] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const queryClient = useQueryClient();

  const { wards, constituencies, counties, regions } = useSelector((state) => ({
    wards: state.location.wards,
    counties: state.location.counties,
    constituencies: state.location.constituencies,
    regions: state.location.regions,
  }));

  const {
    isFacilitator,
    isSiteCord,
    isCountyCoordinator,
    isClusterLeader,
    isRegionalCountyCoordinator,
    isAdmin,
    isSuperAdmin,
    isDirector,
    isData,
    addresses,
  } = useRole();

  const [pagination, setPagination] = useState({
    pageSize: 100,
    pageNumber: 1,
    sortBy: 'id',
    ascendingSort: false,
    wardIds: getSearchColumns()?.find((t) => t?.name === 'wardIds')?.value,
    searchColumns: [
      ...(isFacilitator
        ? [
          // {
          //   name: 'wardId',
          //   value: addresses[0]?.wardId,
          // },
        ]
        : []),
      ...(isCountyCoordinator
        ? [
          {
            name: 'countyId',
            value: addresses[0]?.countyId,
          },
        ]
        : []),
      ...(isClusterLeader
        ? [
          {
            name: 'countyId',
            value: addresses[0]?.countyId,
          },
        ]
        : []),
      ...(isRegionalCountyCoordinator
        ? [
          {
            name: 'regionId',
            value: addresses[0]?.regionId,
          },
        ]
        : []),
      ...(isSiteCord
        ? [
          {
            name: 'constituencyId',
            value: addresses[0]?.constituencyId,
          },
        ]
        : []),
    ],
  });

  useEffect(() => {
    let add = member?.memberId ? member?.addresses?.map((a) => a.wardId) : [];
    let wardIds = isFacilitator
      ? getSearchColumns()?.find((t) => t?.name === 'wardIds')?.value
      : add;
    console.log('Fetching groups for wards:', wardIds);
    mutateGroups({
      ...pagination,
      pageNumber: pagination.pageNumber - 1,
      wardIds: wardIds,
    });
  }, [pagination]);

  const { principalMinDob, principalMaxAge } = useAgeLimits();

  // Define validation schema
  const validationSchema = Yup.object({
    firstName: Yup.string().required('Please Enter First Name'),
    lastName: Yup.string().required('Please Enter Last Name'),
    identificationType: Yup.string().required('Please Select ID Type'),
    identificationNumber: Yup.string()
      .required('Please Enter ID')
      .max(9, 'ID cannot be more than 9 digits'),
    mobileNumber1: Yup.string()
      .required('Please Enter Mobile Number')
      .matches(/^\d+$/, 'Phone number must contain only digits'),
    gender: Yup.string().required('Please Select Gender'),
    dob: Yup.string().required('Please Select DOB')
      .test('max-age', `Member cannot be older than ${principalMaxAge} years`, (value) => {
        if (!value) return true;
        const d = moment(value, ['YYYY-MM-DD', 'DD-MM-YYYY'], true);
        if (!d.isValid()) return true;
        return d.isSameOrAfter(principalMinDob, 'day');
      }),
    joinType: Yup.string().required('Please Select Joining Type'),
    groupId: Yup.string().when('joinType', {
      is: (v) => v === 'Group',
      then: (schema) => schema.required('Please Select Group'),
    }),
  });

  // Initialize formik
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstName: member?.firstName || '',
      lastName: member?.lastName || '',
      email: member?.email || '',
      identificationType: member?.identificationType || '',
      identificationNumber: member?.identificationNumber || '',
      mobileNumber1: member?.mobileNumber1 || '',
      mobileNumber2: member?.mobileNumber2 || '',
      gender: member?.gender || '',
      dob: member?.dob ? moment(member?.dob).format('DD-MM-YYYY') : '',
      joinType: member?.joinType || '',
      status: member?.status || '',
      policyMatured: member?.policyMatured || false,
      groupId: member?.groupId || '',
      showGroup: false,
      membership: member?.membership || 'New',
      regionId: member?.addresses?.[0].regionId || '',
      countyId: member?.addresses?.[0].countyId || '',
      constituencyId: member?.addresses?.[0].constituencyId || '',
      wardId: member?.addresses?.[0].wardId || '',
      processedToday: member?.processedToday || false,
      password: '',
    },
    validationSchema,
    onSubmit: async ({ dob, ...values }) => {
      setIsUpdating(true);
      setErrorMessage(null);
      let memberId = null;
      let adrs = [
        {
          wardId: values?.wardId,
          constituencyId: values?.constituencyId,
          countyId: values?.countyId,
          regionId: values?.regionId,
        },
      ];
      if (!(member?.id || member?.memberId)) {
        const data = {
          ...values,
          membership: 'New',
          dob: moment(dob).format('DD-MM-YYYY'),
          source: member?.source || 'Web',
          app: 'AdminPortal',
          status: 'Draft',
          addresses: adrs,
        };
        // Principal duplicate-ID check here, not on the Next-of-Kin step where the
        // principal+NOK create actually fires (see docs 04-registration-validation).
        const trimmedId = (values.identificationNumber || '').trim();
        try {
          const existing = await getMemberByIdNumberApi(trimmedId);
          if (existing && (existing.memberId || existing.id)) {
            const msg =
              'A member with this Identification Number already exists. Please use Search to find and update that member instead.';
            setErrorMessage(msg);
            Swal.fire('Identification number already exists', msg, 'error');
            setIsUpdating(false);
            return;
          }
        } catch (err) {
          // Non-fatal: the principal+NOK create on the next step still enforces this.
          console.error('Principal ID pre-check failed:', err);
        }
        const trimmedMobile = (values.mobileNumber1 || '').trim();
        try {
          const exists = await getMemberByMobileApi(trimmedMobile);
          if (exists) {
            const msg =
              'A member with this Mobile Number already exists. Please use Search to find and update that member instead.';
            setErrorMessage(msg);
            Swal.fire('Mobile number already exists', msg, 'error');
            setIsUpdating(false);
            return;
          }
        } catch (err) {
          // Non-fatal: the principal+NOK create on the next step still enforces this.
          console.error('Principal mobile pre-check failed:', err);
        }
        if (typeof onSetMember === 'function') {
          onSetMember(data);
          setIsUpdating(false);
          onNext();
          return;
        }
        await addMember(data)
          .then((res) => {
            memberId = res?.data;
            mutateMember(memberId);
            setIsUpdating(false);
            onNext();
          })
          .catch((err) => {
            setErrorMessage(err?.message);
            Swal.fire(err?.status, err?.message, 'error');
            setIsUpdating(false);
          });
      } else {
        const data = {
          ...member,
          ...values,
          app: 'AdminPortal',
          memberId: member?.memberId,
          dob: moment(dob).format('DD-MM-YYYY'),
          source: member?.source || 'Web',
          status: 'Draft',
          addresses: adrs,
        };
        await updateMember(data)
          .then((res) => {
            setIsUpdating(false);
            memberId = res.data?.id;
            // onNext();
          })
          .catch((err) => {
            setIsUpdating(false); 
            setErrorMessage(err?.message);
          });
      }
      onChangeMemberId(memberId);
    },
  });

  const handleUpdate = async () => {
    try {
      setIsUpdating(true);
      if (validation.isValid) {
        setErrorMessage(null);
        const { dob, ...values } = validation.values;
        let adrs = [
          {
            wardId: values?.wardId,
            constituencyId: values?.constituencyId,
            countyId: values?.countyId,
            regionId: values?.regionId,
          },
        ];
        const data = {
          ...member,
          ...values,
          memberId: member?.memberId,
          dob: moment(dob).format('DD-MM-YYYY'),
          addresses: adrs,
          app: 'AdminPortal',
        };
        await updateMember(data)
          .then((res) => {
            const memberId = res.data;
            mutateMember(memberId);
            Swal.fire('Success', 'Member updated successfully', 'success');
            // onNext();
          })
          .catch((err) => {
            setIsUpdating(false);
            setErrorMessage(err?.message);
            Swal.fire(err?.status, err?.message, 'error');
          });
      } else {
        setErrorMessage('Please fill in all required fields correctly.');
        Swal.fire(
          'Error',
          'Please fill in all required fields correctly.',
          'error',
        );
      }
    } catch (error) {
      console.error('Error updating member:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const {
    mutate: mutateGroups,
    data: groupsData,
    isLoading: loadingGroups,
  } = useMutation((payload) => getAllGroupApi(payload), {
    onSuccess: () => { },
    onSettled: () => {
      queryClient.invalidateQueries('/um/group/search');
    },
  });

  const groupOptions =
    groupsData?.list?.map((item) => ({
      value: item.id,
      label: item.name,
    })) || [];

  const fetchAddressData = (addr) => {
    console.log('Fetching address data for:', addr);
    dispatch(
      getRegions({
        ids: [1],
        filter:
          isRegionalCountyCoordinator ||
          isCountyCoordinator ||
          isClusterLeader ||
          isSiteCord ||
          isFacilitator,
        addresses: addr,
      }),
    );
    const uniqueRegionIds = [...new Set(addr?.map((a) => a.regionId))];
    dispatch(
      getCounties({
        ids: uniqueRegionIds,
        filter: isClusterLeader || isSiteCord || isFacilitator || isCountyCoordinator,
        addresses: addr,
      }),
    );
    const uniqueCountyIds = [...new Set(addr?.map((a) => a.countyId))];
    dispatch(
      getSubCounties({
        ids: uniqueCountyIds,
        filter: isSiteCord || isFacilitator || isClusterLeader,
        addresses: addr,
      }),
    );
    const uniqueSubCountyIds = [...new Set(addr?.map((a) => a.constituencyId))];
    dispatch(
      getWards({
        ids: uniqueSubCountyIds,
        filter: isFacilitator,
        addresses: addr,
      }),
    );

    const wardId = isNew ? addresses?.[0].wardId : addr?.[0].wardId;

    (isAdmin || isSuperAdmin || isDirector || isData || addr?.length) &&
      mutateGroups({
        pageSize: 20,
        pageNumber: 0,
        sortBy: 'name',
        wardIds:
          !isData || !isAdmin || !isSuperAdmin
            ? [wardId]
            : getSearchColumns()?.find((t) => t?.name === 'wardIds')?.value,
        searchColumns: [],
      });
  };

  useEffect(() => {
    if (member) {
      if (!isNew) {
        const a = member?.addresses[0];
        fetchAddressData(member?.addresses);
        onChangeMemberId(member?.memberId);
        validation.setValues({
          ...member,
          wardId: a?.wardId,
          constituencyId: a?.constituencyId,
          countyId: a?.countyId,
          regionId: a?.regionId,
        });
      }
    } else {
      console.log('New member detected, fetching address data.');
      console.log('User Addresses for new member:', addresses);
      fetchAddressData(addresses);
    }
  }, [member]);

  const onChangeRegion = (e) => {
    const regionId = e.target.value;
    validation.setFieldValue('regionId', regionId);
    dispatch(
      getCounties({
        ids: [regionId],
        filter: isClusterLeader || isCountyCoordinator,
        addresses,
      }),
    );
  };

  const onChangeCounty = (e) => {
    const countyId = e.target.value;
    validation.setFieldValue('countyId', countyId);
    dispatch(
      getSubCounties({
        ids: [countyId],
        filter: isSiteCord,
        addresses,
      }),
    );
  };

  const onChangeSubCounty = (e) => {
    const constituencyId = e.target.value;
    validation.setFieldValue('constituencyId', constituencyId);
    dispatch(
      getWards({
        ids: [constituencyId],
        filter: isFacilitator,
        addresses,
      }),
    );
  };

  const onChangeWard = (e) => {
    const wardId = e.target.value;
    console.log(wardId);
    validation.setFieldValue('wardId', wardId);
    if (wardId) {
      let wardIds = getSearchColumns()?.find(
        (t) => t?.name === 'wardIds',
      )?.value;
      mutateGroups({
        pageSize: 100,
        pageNumber: 0,
        sortBy: 'name',
        wardIds: wardId ? [wardId] : wardIds,
        searchColumns: [{ name: 'wardId', value: wardId }],
      });
    }
  };

  const makeDebounceSearch = useCallback(
    debounce((value) => {
      const updatedSearchColumns = pagination.searchColumns.map((col) =>
        col.name === 'groupName' ? { ...col, value } : col,
      );

      // If "groupName" is not already in the searchColumns, add it
      if (!updatedSearchColumns.some((col) => col.name === 'groupName')) {
        updatedSearchColumns.push({
          name: 'groupName',
          value: value ?? '',
        });
        updatedSearchColumns.push(
          ...(validation.values.wardId
            ? [
              {
                name: 'wardId',
                value: validation.values.wardId,
              },
            ]
            : []),
        );
      }
      console.log('Updated Search Columns:', updatedSearchColumns);
      setPagination({ ...pagination, searchColumns: updatedSearchColumns });
    }, 600),
    [],
  );

  return (
    <Form
      autoComplete="off"
      onSubmit={(e) => {
        e.preventDefault();
        validation.handleSubmit();
        return false;
      }}
    >
      {errorMessage ? <Alert color="danger">{errorMessage}</Alert> : null}
      <Row>
        <Col sm="6" lg="3">
          <FormGroup className="mb-3">
            <Label for="regionId">Select Region</Label>
            <SelectField
              name="regionId"
              id="regionId"
              value={validation.values.regionId || ''}
              onChange={onChangeRegion}
            >
              <option defaultValue value={''}>
                Select Region...
              </option>
              {regions?.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </SelectField>
          </FormGroup>
        </Col>
        <Col sm="6" lg="3">
          <FormGroup className="mb-3">
            <Label for="countyId">Select County</Label>
            <SelectField
              id="countyId"
              name="countyId"
              value={validation.values.countyId || ''}
              onChange={onChangeCounty}
            >
              <option defaultValue value={''}>
                Select County...
              </option>
              {counties?.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </SelectField>
          </FormGroup>
        </Col>
        <Col sm="6" lg="3">
          <FormGroup className="mb-3">
            <Label for="constituencyId">Select Constituency</Label>
            <SelectField
              name="constituencyId"
              id="constituencyId"
              value={validation.values.constituencyId || ''}
              onChange={onChangeSubCounty}
            >
              <option defaultValue value={''}>
                Select Constituency...
              </option>
              {constituencies?.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </SelectField>
          </FormGroup>
        </Col>
        <Col sm="6" lg="3">
          <FormGroup className="mb-3">
            <Label for="wardId">Select Ward</Label>
            <SelectField
              id="wardId"
              name="wardId"
              value={validation.values.wardId || ''}
              onChange={onChangeWard}
            >
              <option defaultValue value={''}>
                Select Ward...
              </option>
              {wards?.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </SelectField>
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col lg="12">
          <FormGroup className="mb-3">
            <Label for="joinType">Joining Type</Label>
            <SelectField
              name="joinType"
              id="joinType"
              onChange={(e) => {
                const value = e.target.value;
                validation.setFieldValue('joinType', value);

                if (value === 'Group') {
                  validation.setFieldValue('showGroup', true);
                  let wardIds = getSearchColumns()?.find(
                    (t) => t?.name === 'wardIds',
                  )?.value;
                  mutateGroups({
                    pageSize: 100,
                    pageNumber: 0,
                    sortBy: 'name',
                    wardIds: wardIds?.length
                      ? wardIds
                      : [validation.values.wardId],
                    searchColumns: [
                      ...(validation.values.wardId
                        ? [
                          {
                            name: 'wardId',
                            value: validation.values.wardId,
                          },
                        ]
                        : []),
                    ],
                  });
                } else {
                  validation.setFieldValue('showGroup', false);
                }
              }}
              onBlur={validation.handleBlur}
              value={validation.values.joinType || ''}
              invalid={
                validation.touched.joinType && validation.errors.joinType
                  ? true
                  : false
              }
            >
              <option value="" defaultValue>
                Select an option...
              </option>
              <option value="Individual">Individual</option>
              <option value="Group">Group</option>
            </SelectField>
            {validation.touched.joinType && validation.errors.joinType ? (
              <FormFeedback type="invalid">
                {validation.errors.joinType}
              </FormFeedback>
            ) : null}
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md="6">
          <FormGroup className="mb-3">
            <Label for="firstName">First Name</Label>
            <Input
              name="firstName"
              type="text"
              className="form-control"
              id="firstName"
              placeholder="Enter Your First Name"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.firstName || ''}
              invalid={
                validation.touched.firstName && validation.errors.firstName
                  ? true
                  : false
              }
            />
            {validation.touched.firstName && validation.errors.firstName ? (
              <FormFeedback type="invalid">
                {validation.errors.firstName}
              </FormFeedback>
            ) : null}
          </FormGroup>
        </Col>
        <Col md="6">
          <FormGroup className="mb-3">
            <Label for="lastName">Last Name</Label>
            <Input
              name="lastName"
              type="text"
              className="form-control"
              id="lastName"
              placeholder="Enter Your Last Name"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.lastName || ''}
              invalid={
                validation.touched.lastName && validation.errors.lastName
                  ? true
                  : false
              }
            />
            {validation.touched.lastName && validation.errors.lastName ? (
              <FormFeedback type="invalid">
                {validation.errors.lastName}
              </FormFeedback>
            ) : null}
          </FormGroup>
        </Col>
        <Col md="6">
          <FormGroup className="mb-3">
            <Label for="identificationType">Identification Type</Label>
            <SelectField
              id="identificationType"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              name="identificationType"
              value={validation.values.identificationType || ''}
              invalid={
                validation.touched.identificationType &&
                  validation.errors.identificationType
                  ? true
                  : false
              }
            >
              <option defaultValue>Select ID Type...</option>
              <option value="idNumber">National ID Card</option>
              <option value="Passport">Passport</option>
            </SelectField>
            {validation.touched.identificationType &&
              validation.errors.identificationType ? (
              <FormFeedback type="invalid">
                {validation.errors.identificationType}
              </FormFeedback>
            ) : null}
          </FormGroup>
        </Col>
        <Col md="6">
          <FormGroup className="mb-3">
            <Label for="identificationNumber">Identification Number</Label>
            <Input
              name="identificationNumber"
              type="text"
              className="form-control"
              id="identificationNumber"
              placeholder="Enter Your Identification Number"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.identificationNumber || ''}
              invalid={
                validation.touched.identificationNumber &&
                  validation.errors.identificationNumber
                  ? true
                  : false
              }
            />
            {validation.touched.identificationNumber &&
              validation.errors.identificationNumber ? (
              <FormFeedback type="invalid">
                {validation.errors.identificationNumber}
              </FormFeedback>
            ) : null}
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md="6">
          <FormGroup className="mb-3">
            <Label for="dob">Date Of birth</Label>
            <DateField
              name="dob"
              max={moment().subtract(18, 'years').format('YYYY-MM-DD')}
              min={principalMinDob}
              id="dob"
              placeholder="Select Date of Birth"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.dob || ''}
              invalid={
                validation.touched.dob && validation.errors.dob ? true : false
              }
            />
            {validation.touched.dob && validation.errors.dob ? (
              <FormFeedback type="invalid">
                {validation.errors.dob}
              </FormFeedback>
            ) : null}
          </FormGroup>
        </Col>
        <Col md="6">
          <FormGroup className="mb-3">
            <Label for="gender">Sex</Label>
            <SelectField
              name="gender"
              id="gender"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.gender || ''}
              invalid={
                validation.touched.gender && validation.errors.gender
                  ? true
                  : false
              }
            >
              <option value="" disabled>
                Select Gender...
              </option>
              <option value="Female">Female</option>
              <option value="Male">Male</option>
              <option value="Intersex">Intersex</option>
              <option value="Other">Other</option>
            </SelectField>
            {validation.touched.gender && validation.errors.gender ? (
              <FormFeedback type="invalid">
                {validation.errors.gender}
              </FormFeedback>
            ) : null}
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md="6">
          <FormGroup className="mb-3">
            <Label for="mobileNumber1">Phone Number</Label>
            <Input
              name="mobileNumber1"
              type="text"
              className="form-control"
              id="mobileNumber1"
              placeholder="Enter Your Mobile Number"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.mobileNumber1 || ''}
              invalid={
                validation.touched.mobileNumber1 &&
                  validation.errors.mobileNumber1
                  ? true
                  : false
              }
            />
            {validation.touched.mobileNumber1 &&
              validation.errors.mobileNumber1 ? (
              <FormFeedback type="invalid">
                {validation.errors.mobileNumber1}
              </FormFeedback>
            ) : null}
          </FormGroup>
        </Col>
        <Col md="6">
          <FormGroup className="mb-3">
            <Label for="mobileNumber2">Alternate Phone Number</Label>
            <Input
              name="mobileNumber2"
              type="number"
              className="form-control"
              id="mobileNumber2"
              placeholder="Enter Alternate Mobile Number"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.mobileNumber2 || ''}
            />
          </FormGroup>
        </Col>
        <Col md="6">
          <FormGroup className="mb-3">
            <Label for="email">Enter Email Address</Label>
            <Input
              name="email"
              type="email"
              className="form-control"
              id="email"
              placeholder="Enter Email Address"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.email || ''}
            />
          </FormGroup>
        </Col>

        {validation.values.joinType === 'Group' && (
          <Col md="6">
            <FormGroup className="mb-3">
              <Label for="groupId">Select Group</Label>
              <Select
                id="groupId"
                name="groupId"
                placeholder={
                  loadingGroups ? 'Loading data...' : 'Search Group ....'
                }
                options={groupOptions}
                value={groupOptions.find(
                  (option) => option.value === validation.values.groupId,
                )}
                onChange={(selectedOption) =>
                  validation.setFieldValue('groupId', selectedOption?.value)
                }
                onBlur={validation.handleBlur}
                onInputChange={(e) => {
                  validation.handleChange(e);
                  makeDebounceSearch(e);
                }}
                className={
                  validation.touched.groupId && validation.errors.groupId
                    ? 'is-invalid'
                    : ''
                }
                isLoading={loadingGroups}
              />
              {validation.touched.groupId && validation.errors.groupId && (
                <FormFeedback type="invalid">
                  {validation.errors.groupId}
                </FormFeedback>
              )}
            </FormGroup>
          </Col>
        )}
        {isSuperAdmin && (
          <>
            <Col sm="6" lg="3">
              <FormGroup className="mb-3">
                <Label for="statusSelect">Select Status</Label>
                <SelectField
                  name="status"
                  id="statusSelect"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.status || ''}
                  invalid={
                    validation.touched.status && validation.errors.status
                      ? true
                      : false
                  }
                >
                  <option value="" disabled>
                    Select Status...
                  </option>
                  <option value="Active">Active</option>
                  <option value="Pending">Pending</option>
                  <option value="Dormant">Dormant</option>
                  <option value="Defaulted">Defaulted</option>
                  <option value="Draft">Draft</option>
                  <option value="New">New</option>
                  <option value="Deceased">Deceased</option>
                </SelectField>
                {validation.touched.status && validation.errors.status ? (
                  <FormFeedback type="invalid">
                    {validation.errors.status}
                  </FormFeedback>
                ) : null}
              </FormGroup>
            </Col>
            <Col sm="6" lg="3">
              <FormGroup className="mb-3">
                <Label for="policyMatured">Policy Matured</Label>
                <SelectField
                  name="policyMatured"
                  id="policyMatured"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={
                    validation.values.policyMatured === true ? 'true' : 'false'
                  }
                  invalid={
                    validation.touched.policyMatured &&
                      validation.errors.policyMatured
                      ? true
                      : false
                  }
                >
                  <option value="" disabled>
                    Select Option...
                  </option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </SelectField>
                {validation.touched.policyMatured &&
                  validation.errors.policyMatured ? (
                  <FormFeedback type="invalid">
                    {validation.errors.policyMatured}
                  </FormFeedback>
                ) : null}
              </FormGroup>
            </Col>
            <Col sm="6" lg="3">
              <FormGroup className="mb-3">
                <Label for="policyMatured">Processed Today</Label>
                <SelectField
                  name="processedToday"
                  id="processedToday"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={
                    validation.values.processedToday === true ? 'true' : 'false'
                  }
                  invalid={
                    validation.touched.processedToday &&
                      validation.errors.processedToday
                      ? true
                      : false
                  }
                >
                  <option value="" disabled>
                    Select Option...
                  </option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </SelectField>
                {validation.touched.processedToday &&
                  validation.errors.processedToday ? (
                  <FormFeedback type="invalid">
                    {validation.errors.processedToday}
                  </FormFeedback>
                ) : null}
              </FormGroup>
            </Col>
            <Col sm="6" lg="3">
              <Label className="form-label">Password</Label>
              <Input
                name="password"
                value={validation?.values?.password || ''}
                type="password"
                placeholder="Enter Password"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={
                  validation.touched.password && validation.errors.password
                    ? true
                    : false
                }
              />
              {validation.touched.password && validation.errors.password ? (
                <FormFeedback type="invalid">
                  {validation.errors.password}
                </FormFeedback>
              ) : null}
            </Col>
          </>
        )}
      </Row>

      <div className="wizard-actions">
        {isSuperAdmin && (
          <Button
            onClick={handleUpdate}
            disabled={isUpdating}
            type="submit"
            color="success"
          >
            <i className="mdi mdi-content-save-outline me-1" />
            Update member
          </Button>
        )}
        <Button disabled={isUpdating} type="submit" color="primary">
          {isNew ? 'Save & continue' : 'Save draft'}
          <i className="mdi mdi-chevron-right ms-1" />
        </Button>
      </div>
    </Form>
  );
};

export default MemberDetailsTab;
